import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import primelocaleEn from 'primelocale/en.json';
import primelocaleEs from 'primelocale/es.json';
import { PrimeNG } from 'primeng/config';
import type { AppLanguage } from '../../i18n/languages';
import { matchSupportedLanguage } from '../../i18n/languages';
import { LanguagePreferenceService } from './language-preference.service';

const PRIMENG_LOCALES: Record<AppLanguage, object> = {
	es: primelocaleEs.es,
	en: primelocaleEn.en,
};

@Injectable({ providedIn: 'root' })
export class LanguageInitService {
	private readonly transloco = inject(TranslocoService);
	private readonly primeNG = inject(PrimeNG);
	private readonly preference = inject(LanguagePreferenceService);

	async initializeLanguage(): Promise<void> {
		const lang = this.preference.resolveInitial();
		this.preference.apply(lang);
		this.updatePrimeNGLocale(lang);
		this.transloco.langChanges$.subscribe((next) => {
			this.updatePrimeNGLocale(matchSupportedLanguage(next) ?? 'es');
			document.documentElement.lang = next;
		});
		await this.waitForTranslations(lang);
	}

	private updatePrimeNGLocale(lang: AppLanguage): void {
		this.primeNG.setTranslation(PRIMENG_LOCALES[lang]);
	}

	private async waitForTranslations(lang: string): Promise<void> {
		return new Promise((resolve) => {
			const existing = this.transloco.getTranslation(lang);
			if (existing && Object.keys(existing).length > 0) {
				resolve();
				return;
			}
			let settled = false;
			const done = () => {
				if (settled) return;
				settled = true;
				resolve();
			};
			const subscription = this.transloco.load(lang).subscribe({ next: done, error: done });
			setTimeout(() => {
				subscription.unsubscribe();
				done();
			}, 3000);
		});
	}
}
